import { useState, useEffect } from 'react';
import { Command } from 'cmdk';
import { Search, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useGlobalSearch, SearchResult } from '@/hooks/useGlobalSearch';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface GlobalCommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const getTypeColor = (type: SearchResult['type']) => {
  switch (type) {
    case 'campaign': return 'bg-primary/10 text-primary border-primary/20';
    case 'lead': return 'bg-accent text-accent-foreground border-accent/20';
    case 'agent': return 'bg-secondary text-secondary-foreground border-secondary/20';
    default: return 'bg-muted text-muted-foreground';
  }
};

export const GlobalCommandPalette = ({ open, onOpenChange }: GlobalCommandPaletteProps) => {
  const [search, setSearch] = useState('');
  const { results, loading } = useGlobalSearch(search);
  const navigate = useNavigate();

  const handleSelect = (result: SearchResult) => {
    navigate(result.href);
    onOpenChange(false);
    setSearch('');
  };

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [open, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0 shadow-lg max-w-2xl">
        <Command className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          <div className="flex items-center border-b px-3" cmdk-input-wrapper="">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50 bg-transparent" />
            <Command.Input
              value={search}
              onValueChange={setSearch}
              className="flex h-12 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="Search campaigns, leads, agents... (⌘K)"
            />
          </div>
          <Command.List className="max-h-80 overflow-y-auto">
            {loading && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Searching...
              </div>
            )}
            {!loading && search && results.length === 0 && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No results found for "{search}"
              </div>
            )}
            {!search && !loading && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Start typing to search across campaigns, leads, and agents
              </div>
            )}
            {results.length > 0 && (
              <Command.Group heading="Results">
                {results.map((result) => (
                  <Command.Item
                    key={result.id}
                    value={result.title}
                    onSelect={() => handleSelect(result)}
                    className="flex items-center gap-3 px-3 py-3 cursor-pointer rounded-sm hover:bg-accent hover:text-accent-foreground data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground"
                  >
                    <span className="text-lg bg-transparent">{result.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{result.title}</span>
                        <Badge variant="secondary" className={cn("text-xs", getTypeColor(result.type))}>
                          {result.type}
                        </Badge>
                      </div>
                      {result.subtitle && (
                        <div className="text-sm text-muted-foreground truncate">
                          {result.subtitle}
                        </div>
                      )}
                    </div>
                    <ArrowRight className="h-4 w-4 opacity-50 bg-transparent" />
                  </Command.Item>
                ))}
              </Command.Group>
            )}
          </Command.List>
          <div className="border-t p-2 text-xs text-muted-foreground">
            <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100">
              <span className="text-xs">⌘</span>K
            </kbd>{' '}
            to toggle • 
            <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100">
              ↵
            </kbd>{' '}
            to select • 
            <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100">
              ↑↓
            </kbd>{' '}
            to navigate
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
};